import { InputAdornment, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { GRANTOR_RELATIONSHIP_OPTIONS } from '@/components/form/PersonFormFragment/constants';
import { User01Icon } from '@/components/icons/User01Icon';
import { UserCheck01Icon } from '@/components/icons/UserCheck01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import {
  AIOnboardingIndividual_SELF,
  AIOnboardingModalFormShape,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { FetchAiOnboardingModalDataV2Document } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { EntityDocumentViewerDocument } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { COLORS } from '@/styles/tokens/colors';
import { ClientProfileRelationshipType } from '@/types/schema';
import { PathsOf } from '@/types/subform';

interface IndividualFieldsRowProps {
  index: number;
  onCreatedFromSuggestion: () => void;
}

export function IndividualFieldsRow({
  index,
  onCreatedFromSuggestion,
}: IndividualFieldsRowProps) {
  const { styles, columns } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS.individuals;

  const { control } = useFormContext<AIOnboardingModalFormShape>();

  const {
    household: { id, possiblePrimaryClients },
  } = useAIOnboardingModalContext();

  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);

  const {
    relationshipToPrimaryClient1,
    relationshipToPrimaryClient2,
    firstName,
    middleName,
    lastName,
    suffix,
    _suggestionID,
  } = useWatch({
    control,
    name: `individuals.${index}` as const satisfies PathsOf<AIOnboardingModalFormShape>,
  });

  const showAddAsClientButton = useMemo(() => {
    return (
      (relationshipToPrimaryClient1 === ClientProfileRelationshipType.Spouse ||
        relationshipToPrimaryClient1 ===
          ClientProfileRelationshipType.DomesticPartner) &&
      possiblePrimaryClients.length === 1
    );
  }, [possiblePrimaryClients.length, relationshipToPrimaryClient1]);

  const { markedAsPrimary1, markedAsPrimary2, markedAsPrimary } =
    useMemo(() => {
      const markedAsPrimary1 =
        relationshipToPrimaryClient1 === AIOnboardingIndividual_SELF;

      const markedAsPrimary2 =
        relationshipToPrimaryClient2 === AIOnboardingIndividual_SELF;

      return {
        markedAsPrimary1,
        markedAsPrimary2,
        markedAsPrimary: markedAsPrimary1 || markedAsPrimary2,
      };
    }, [relationshipToPrimaryClient1, relationshipToPrimaryClient2]);

  const initialRelationships = useMemo(() => {
    return {
      // Set the initial relationship mapping to the other primary client.
      // The id and the type are guaranteed to be non-empty at the time we open the modal.
      [possiblePrimaryClients[0]?.id || '']:
        relationshipToPrimaryClient1 as ClientProfileRelationshipType,
    };
  }, [possiblePrimaryClients, relationshipToPrimaryClient1]);

  return (
    <>
      <FormLayoutRow sx={{ gap: styles.columnPaddingHorizontal }}>
        <FormLayoutItem width={columns.firstName.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `individuals.${index}.firstName` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.firstName.columnHeader}
            hideLabel
            required
            showErrorBeforeSubmit
            disabled={markedAsPrimary}
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.middleName.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `individuals.${index}.middleName` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.middleName.columnHeader}
            hideLabel
            disabled={markedAsPrimary}
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.lastName.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `individuals.${index}.lastName` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.lastName.columnHeader}
            hideLabel
            required
            showErrorBeforeSubmit
            disabled={markedAsPrimary}
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.suffix.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `individuals.${index}.suffix` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.suffix.columnHeader}
            hideLabel
            disabled={markedAsPrimary}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow sx={{ marginTop: -2 }}>
        <FormLayoutItem
          width={columns.relationshipToPrimaryClient1.columnWidth}
        >
          <RelationshipSelectInput
            fieldName={
              `individuals.${index}.relationshipToPrimaryClient1` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            primaryClientDisplayName={
              possiblePrimaryClients[0]?.displayName || 'Primary Client 1'
            }
            disabled={markedAsPrimary2}
          />
        </FormLayoutItem>
        {showAddAsClientButton && !markedAsPrimary && (
          <FormLayoutItem
            width={columns.relationshipToPrimaryClient1.columnWidth}
          >
            <Button
              size="sm"
              variant="primary"
              onClick={() => setIsAddClientModalOpen(true)}
            >
              Add as client
            </Button>
          </FormLayoutItem>
        )}
        {possiblePrimaryClients.length > 1 && (
          <FormLayoutItem
            width={columns.relationshipToPrimaryClient2.columnWidth}
          >
            <RelationshipSelectInput
              fieldName={
                `individuals.${index}.relationshipToPrimaryClient2` as const satisfies PathsOf<AIOnboardingModalFormShape>
              }
              primaryClientDisplayName={
                possiblePrimaryClients[1]?.displayName || 'Primary Client 2'
              }
              disabled={markedAsPrimary1}
            />
          </FormLayoutItem>
        )}
      </FormLayoutRow>
      {isAddClientModalOpen && (
        <ClientProfileSideDrawer
          isOpen={isAddClientModalOpen}
          setIsOpen={setIsAddClientModalOpen}
          clientProfileId={null}
          householdId={id}
          clientProfileTypeDisplay="individual"
          isPrimary={true}
          forceGrantorIfNoClientProfileId={true}
          initialClientProfileName={{
            firstName,
            middleName,
            lastName,
            suffix,
          }}
          initialRelationships={initialRelationships}
          ignoredQueryDocuments={[
            EntityDocumentViewerDocument,
            FetchAiOnboardingModalDataV2Document,
          ]}
          createdFromSuggestionID={_suggestionID}
          onAfterCreate={onCreatedFromSuggestion}
        />
      )}
    </>
  );
}

interface RelationshipSelectInputProps {
  fieldName: PathsOf<AIOnboardingModalFormShape>;
  primaryClientDisplayName: string;
  disabled: boolean;
}

function RelationshipSelectInput({
  fieldName,
  primaryClientDisplayName,
  disabled,
}: RelationshipSelectInputProps) {
  const { control } = useFormContext<AIOnboardingModalFormShape>();

  const label = `Relationship to ${primaryClientDisplayName}`;

  const options = useMemo(() => {
    if (!primaryClientDisplayName) return [];

    return [
      {
        display: `Mark as ${primaryClientDisplayName}`,
        value: AIOnboardingIndividual_SELF,
        startAdornment: (
          <UserCheck01Icon
            size={16}
            sx={{ mr: 1 }}
            color={COLORS.TEXT.SECONDARY}
          />
        ),
        withDividerBelow: true,
      },
      ...GRANTOR_RELATIONSHIP_OPTIONS.map((option) => ({
        display: `${option.display} of ${primaryClientDisplayName}`,
        value: option.value,
      })),
    ];
  }, [primaryClientDisplayName]);

  return (
    <FormAwareSelectInput<AIOnboardingModalFormShape>
      control={control}
      fieldName={fieldName}
      label={label}
      options={options}
      hideLabel
      startAdornment={
        <InputAdornment position="start">
          <User01Icon size={16} color={COLORS.GRAY['500']} />
        </InputAdornment>
      }
      disabled={disabled}
      renderValue={(value) => {
        const selectedOption = options.find((o) => o.value === value);

        if (!value || !selectedOption) {
          return (
            <Typography
              variant="body-xs"
              color={COLORS.GRAY['500']}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {label}
            </Typography>
          );
        }

        if (value === AIOnboardingIndividual_SELF) {
          return (
            <Typography
              variant="body-xs"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              Marked as {primaryClientDisplayName}
            </Typography>
          );
        }

        return (
          <Typography
            variant="body-xs"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {selectedOption.display}
          </Typography>
        );
      }}
    />
  );
}
